import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, map } from 'rxjs';

/** One PDF export job shown in the global downloads stack. */
export interface PdfExportDownloadJob {
  id: string;
  /** When set, knowledge PDF export can disable the export button only for this unit. */
  propertyId?: string;
  progress: number;
  phase: 'working' | 'done' | 'error';
  statusKey: string;
  statusParams?: Record<string, string | number>;
  filename: string | null;
  blobUrl: string | null;
}

@Injectable({ providedIn: 'root' })
export class PdfExportDownloadService {
  private readonly jobsSubject = new BehaviorSubject<PdfExportDownloadJob[]>([]);
  private readonly stackMinimizedSubject = new BehaviorSubject<boolean>(false);

  readonly jobs$ = this.jobsSubject.asObservable();
  readonly stackMinimized$ = this.stackMinimizedSubject.asObservable();

  readonly ui$ = combineLatest([this.jobs$, this.stackMinimized$]).pipe(
    map(([jobs, stackMinimized]) => ({ jobs, stackMinimized })),
  );

  beginJob(
    initial?: Partial<
      Pick<
        PdfExportDownloadJob,
        'progress' | 'statusKey' | 'statusParams' | 'propertyId'
      >
    >,
  ): string {
    const id = crypto.randomUUID();
    const job: PdfExportDownloadJob = {
      id,
      propertyId: initial?.propertyId,
      progress: initial?.progress ?? 5,
      phase: 'working',
      statusKey:
        initial?.statusKey ?? 'units.knowledgeBase.exportPdfPanelFetching',
      statusParams: initial?.statusParams,
      filename: null,
      blobUrl: null,
    };
    this.jobsSubject.next([...this.jobsSubject.value, job]);
    return id;
  }

  hasJob(id: string): boolean {
    return this.jobsSubject.value.some((j) => j.id === id);
  }

  patchJob(id: string, patch: Partial<PdfExportDownloadJob>): void {
    this.jobsSubject.next(
      this.jobsSubject.value.map((j) =>
        j.id === id ? { ...j, ...patch } : j,
      ),
    );
  }

  failJob(id: string): void {
    if (!this.hasJob(id)) return;
    this.patchJob(id, {
      phase: 'error',
      progress: 0,
      statusKey: 'units.knowledgeBase.exportPdfPanelError',
    });
  }

  removeJob(id: string): void {
    const job = this.jobsSubject.value.find((j) => j.id === id);
    if (job?.blobUrl) URL.revokeObjectURL(job.blobUrl);
    this.jobsSubject.next(
      this.jobsSubject.value.filter((j) => j.id !== id),
    );
  }

  dismissAllJobs(): void {
    for (const j of this.jobsSubject.value) {
      if (j.blobUrl) URL.revokeObjectURL(j.blobUrl);
    }
    this.jobsSubject.next([]);
  }

  toggleStackMinimized(): void {
    this.stackMinimizedSubject.next(!this.stackMinimizedSubject.value);
  }

  openJobInBrowser(job: PdfExportDownloadJob): void {
    if (!job.blobUrl) return;
    window.open(job.blobUrl, '_blank', 'noopener,noreferrer');
  }
}
